import { FC, useState, useEffect, useMemo } from "react"
import { useQueryClient } from "react-query"

import { Formik, Form, FormikValues, FormikProps } from "formik"
import * as Yup from 'yup'
import {
  StatusErrorMessage,
  FormikInspectorAgreeCheck,
  initBillingDetails,
  billingDetailsSchema,
  FormikBillingDetails,
  ParseBillingFields,
  FormikNotesArea,
} from "../../../../../components/widgets"

import { SubmitButtonRow, VerdictSelector } from "../../../../../components/layout"

import { doFormSubmit, isPopulatedArray, processNoteValues } from "../../../../../components/helpers"

import { pickBy, cloneDeep } from "lodash"
import { submitDLMPhase_3_1_2_2, DLMROI, extractLabelSelectionDetails, DLMLabelSelectionDetails, DLMLabelSelectorModal } from "../../../../core/doclabel"

const initSubmitInspectionValues = {
  verdict: '',
  appType: '',
  note: '',
  noteAttachments: [],  
  labelDetails: [],
  labelsNotUsed:[],
  inspectorAgree: false,      
}

const schema = Yup.object({
  verdict: Yup.string().trim(),
  note: Yup.string().trim().label('Note')
  .when('verdict', {
      is: (val: string) => (val !== 'approved'),
      then: (schema) => schema.required(),
  }),
  noteAttachments: Yup.array(),
  labelsDetails: Yup.array(),
  labelsNotUsed: Yup.array(),
  inspectorAgree: Yup.bool().label('Confirmation')
  .when('verdict', {
    is: (val: string) => (val === 'approved'),
    then: (schema) => schema.oneOf([true], 'Must be checked'),
  }),
}).concat(billingDetailsSchema);


const SubmitInspectionFormContent: FC<FormikProps<any> & { doClose: Function }> = ({status, setFieldValue, validateField, setStatus, values, doClose}) => {
  
  const [ verdict, setVerdict ] = useState<string | null>(null);
  
  const [ select_codes, setSelectCodes ] = useState<Array<string> | null>(null);
  const [ target_codes, setTargetCodes ] = useState<Array<string> | null>(null);
  const [ selected_series, setSelectedSeries] = useState(-1);

  const { labelsNotUsed, labelDetails, verdict: verdictValue } = values;  
  
  const handleLabelSelect = (selected: Array<string>) => {
    if (! (selected_series > -1 && select_codes && select_codes.length > 0)) return;
    let _labelDetails = cloneDeep(labelDetails);

    let _selected = cloneDeep(labelDetails[selected_series]);
    Object.assign(_selected, { targetLabels: (selected && selected.length > 0) ? selected : null });

    _labelDetails.splice(selected_series, 1, _selected);

    let _labelsNotUsed: Array<string> = [];
    _labelDetails.forEach( (_details: DLMLabelSelectionDetails) => {
      if(! (_details?.targetLabels && _details.targetLabels.length > 0)) return;
      _labelsNotUsed = _labelsNotUsed.concat(_details.targetLabels);
    })

    _labelsNotUsed.sort();

    setFieldValue('labelsNotUsed', _labelsNotUsed, true);
    setFieldValue('labelDetails',_labelDetails);
    setSelectedSeries(-1);
    setSelectCodes(null);
  }

  useEffect(() => {
    setFieldValue('billingRequired', (verdict && ['approved','rejected'].includes(verdict)) ? 'YES' : 'NO');    
  }, [verdict, setFieldValue]);

  useEffect(() => {
    validateField('inspectorAgree');
    validateField('note');
  }, [verdictValue, validateField])

  return (
  <>
  <Form id='kt_doclabel_submitresponse_form' className='form' noValidate>
    <div
      className='d-flex flex-column p-2'
    >
      <VerdictSelector
        verdict={verdict}
        setVerdict={setVerdict}
        setFieldValue={setFieldValue}
        setStatus={setStatus}
        hasRequest
      />

      { verdict &&
      <>
      { (verdict === 'approved' && labelDetails && labelDetails.length > 0) &&
      <>
      <div className='fw-bold fs-6'>
        Labels Not Used:
        { (labelsNotUsed && labelsNotUsed.length > 0) && <span className="ms-2">{`(${labelsNotUsed.length})`}</span>}
      </div>
      <div className="">
      { (labelsNotUsed && labelsNotUsed.length > 0) ? (
        <span className="fs-7">{labelsNotUsed.join(' | ')}</span>
      ) : (
        <span className="text-muted fst-italic">(no selection)</span>
      )
      }
      </div>

      <div className='text-muted fs-7 mt-4'>
        Select from Label Series
      </div>
      <div className='separator border-gray-200 my-0'></div>
      <div className='table-responsive min-h-80px mh-300px'>
        <table
          id='kt_table_roisplit_labels'
          className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer'
        >
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                <th className="p-2 w-125px">Series</th>
                <th className="p-2 w-100px">Action</th>
                <th className="p-2">NOT USED</th>
            </tr>
          </thead>
          { (labelDetails && labelDetails.length > 0) &&
          <tbody className='text-gray-800 align-middle fs-6'>
            {
            labelDetails.map((obj: any , idx: number) => (
              <tr key={`labelDetail_${idx}`} className="labelRow" data-idx={idx} >
                <td className="p-2 align-top fs-7">
                  <>
                  {obj.labelFrom}
                  -
                  {obj.labelTo}
                  </>
                </td>
                <td className="p-2 align-top">
                  <button
                    type="button"
                    className="siteBtn btn btn-light btn-active-primary px-3 py-1 fs-7"
                    onClick={() => {
                        setSelectedSeries(idx);
                        setSelectCodes(obj.labelCodes)
                        setTargetCodes(obj.targetLabels ? obj.targetLabels : [])
                    }}
                    title="Select Label Split"
                  >
                    Select
                  </button>
                  {
                  (obj?.targetLabels && obj.targetLabels.length > 0) &&
                  <div className="d-inline-block text-muted fs-7 mt-1 ms-1">
                    ({obj.targetLabels.length})
                  </div>
                  }
                </td>
                <td className="p-2 align-top fs-7">
                  {
                  (obj?.targetLabels && obj.targetLabels.length > 0) ? (
                    <span className="fs-7">{obj.targetLabels.join(' | ')}</span>
                  ) : (
                    <span className="text-muted fst-italic">(no selection)</span>
                  )
                  }
                </td>
              </tr>
            ))
            }
          </tbody>
          }
        </table>
      </div>
      <div className='separator border-gray-200 my-0'></div>
      </>
      }
      
      <FormikBillingDetails />

      <FormikNotesArea required={verdict !== 'approved'} />

      { (verdict === 'approved') &&
      <FormikInspectorAgreeCheck />
      }

      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={doClose} />
      </>
      }
    </div>
  </Form>
  { (select_codes !== null) &&
  <DLMLabelSelectorModal
    title="Select Labels Not Used"
    subTitle="Select labels found to be unused."
    labelCodes={select_codes}
    targetLabels={target_codes}
    doClose={() => { setSelectCodes(null)}}
    doSubmit={handleLabelSelect}
  />
  }
  </>
  )
}

type Props = {
    id: number
    appID: number
    rois: Array<DLMROI>
    doClose: Function
}

const SubmitInspectionForm: FC<Props> = ({id, appID, rois, doClose}) => {
    const queryClient = useQueryClient()

    const initValues = useMemo(() => {        
      let _labelDetails: Array<DLMLabelSelectionDetails> = [];
      if (rois && isPopulatedArray(rois)) {
        rois.forEach( roi => {
          if (! (roi?.roiDetails && roi.roiDetails.length > 0)) return;
          const _details = extractLabelSelectionDetails(roi.roiDetails);
          if (! (_details && _details.length > 0)) return;

          _labelDetails = _labelDetails.concat(_details);
        })
      }
      
      return {            
        ...cloneDeep(initSubmitInspectionValues),
        ...cloneDeep(initBillingDetails),
        labelDetails: _labelDetails,
      };
    }, [rois])

    const doSubmit = async (values: any, actions: FormikValues) => {

      const { setStatus, setSubmitting } = actions;

      let payload = {
        ...pickBy(values, (value, key) => (value && (! key.match(/(note|AttachmentName|verdict|inspectorAgree)$/i)))),
        siteApplicationid: id,
        approved: (values?.verdict === 'approved')
      };

      processNoteValues(values, payload);
      
      if (values?.verdict === 'request') {
        Object.assign(payload, { rfi: true });
      }

      payload = ParseBillingFields(payload, values);

      await doFormSubmit(submitDLMPhase_3_1_2_2, payload, setStatus, setSubmitting, () => {
        queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'label-applications-list'}]);
        queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
        queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
        doClose();            
      });
    }

    useEffect(() => {
      
    },[rois])    

    return(
    <Formik
      initialValues={initValues}
      validationSchema={schema}
      enableReinitialize={true}
      onSubmit={doSubmit}
      component={(props: any) => <SubmitInspectionFormContent doClose={doClose} {...props} />}
    />
  )
}

export { SubmitInspectionForm }